export const ANALYTICS_EVENT_NAMES = [
  "visitor",
  "page_view",
  "practice_started",
  "signup",
  "login",
  "free_trial_started",
  "interview_started",
  "interview_completed",
  "feedback_generated",
  "pricing_viewed",
  "data_analytics_pricing_viewed",
  "cloud_devops_pricing_viewed",
  "mlops_pricing_viewed",
  "security_pricing_viewed",
  "checkout_started",
  "purchase",
  "payment_failed",
  "free_15_minute_interview_section_viewed",
  "cta_clicked",
  "mock_interview_funnel_step",
  "dussehra_page_viewed",
  "dussehra_hero_viewed",
  "dussehra_offer_viewed",
  "dussehra_opportunity_viewed",
  "dussehra_faq_viewed",
  "dussehra_footer_viewed",
  "dussehra_cta_clicked",
  "dussehra_checkout_started",
  // Role campaign landers. One triple per role so each page is countable on its own.
  // Names stay under GA4's 40-character event-name limit, which is why they are not
  // derived from the (longer) analyticsSource string. Keep in step with
  // meritlens-analytics-service ALLOWED_EVENT_NAMES and the API's EVENT_CATALOG.
  "data_analytics_page_viewed",
  "data_analytics_cta_clicked",
  "data_analytics_checkout_started",
  "cloud_devops_page_viewed",
  "cloud_devops_cta_clicked",
  "cloud_devops_checkout_started",
  "mlops_page_viewed",
  "mlops_cta_clicked",
  "mlops_checkout_started",
  "security_page_viewed",
  "security_cta_clicked",
  "security_checkout_started",
] as const;

export type AnalyticsEventName = (typeof ANALYTICS_EVENT_NAMES)[number];

/**
 * Pricing-overlay event per role campaign lander.
 *
 * Each lander gets its own name so the marketing Events page can report it as a
 * separate row (and click through to that lander's leads) instead of folding all
 * four into the generic ``pricing_viewed``.
 */
export const CAMPAIGN_PRICING_VIEWED_EVENTS = {
  data_analytics_engineering: "data_analytics_pricing_viewed",
  cloud_devops_platform_engineering: "cloud_devops_pricing_viewed",
  mlops_forward_deployed_engineering: "mlops_pricing_viewed",
  security_engineering: "security_pricing_viewed",
} as const;

export type CampaignPricingViewedEvent =
  (typeof CAMPAIGN_PRICING_VIEWED_EVENTS)[keyof typeof CAMPAIGN_PRICING_VIEWED_EVENTS];

export function campaignPricingViewedEvent(
  analyticsSource: string,
): CampaignPricingViewedEvent | undefined {
  return (
    CAMPAIGN_PRICING_VIEWED_EVENTS as Record<
      string,
      CampaignPricingViewedEvent | undefined
    >
  )[analyticsSource];
}

export const FREE_15_MINUTE_INTERVIEW_SECTIONS = [
  "hero",
  "free_15_minute_offer",
  "what_you_get",
  "how_it_works",
  "see_it_in_action",
  "why_it_works",
] as const;

export type AnalyticsPageSection =
  (typeof FREE_15_MINUTE_INTERVIEW_SECTIONS)[number];

export const DUSSEHRA_CTA_NAMES = [
  "discover_benefits",
  "hero_claim",
  "opportunity_claim",
] as const;

export type DussehraCtaName = (typeof DUSSEHRA_CTA_NAMES)[number];

export const MOCK_INTERVIEW_FUNNEL_SETUP_STEPS = [
  "start_clicked",
  "resume_ready",
  "continue_clicked",
  "option_selected",
  "entry_selected",
] as const;

export type MockInterviewFunnelSetupStep =
  (typeof MOCK_INTERVIEW_FUNNEL_SETUP_STEPS)[number];

/**
 * Which door into the paid mock interview the candidate took: the resume upload at
 * `/mock-interview` or role + skills at `/mock-interview/role-based-interview`.
 *
 * Both create the same `resume_based` UserInterview, so the row cannot tell them apart.
 * This property is the only place the distinction is recorded — it rides in
 * `analytics_events.properties`, which has no schema, so no migration is involved.
 */
export type MockInterviewEntry = "resume" | "role_based";

export const FREE_15_MINUTE_INTERVIEW_PAGE_TYPE = "free_15_minute_interview";
export type Free15MinuteInterviewPageType =
  typeof FREE_15_MINUTE_INTERVIEW_PAGE_TYPE;

/**
 * Event-specific parameters stored in `analytics_events.properties` (JSONB).
 *
 * Common metadata (anonymousId, sessionId, userId, UTM, page URL, device, …)
 * stays on first-class event fields — never duplicate it here.
 *
 * Naming is camelCase. Keep values small and non-sensitive.
 * Add a new optional field to the matching event to extend the contract;
 * the database schema does not change.
 */
/**
 * Shape shared by the four role campaign landers. `campaign` carries the route's
 * `analyticsSource` (e.g. `data_analytics_engineering`) so a report can slice the same
 * event name across landers even though each lander keeps its own event names.
 */
export type CampaignLanderPageViewedProperties = {
  campaign?: string;
  pageType?: string;
};

export type CampaignLanderCtaClickedProperties = {
  campaign?: string;
  ctaSurface?: string;
  journey?: string;
};

export type CampaignLanderCheckoutStartedProperties = {
  campaign?: string;
  pageType?: string;
};

export type AnalyticsEventPropertiesByName = {  visitor: Record<string, never>;
  page_view: {
    pageType?: string;
    path?: string;
    query?: string;
  };
  practice_started: {
    practiceType?: string;
    difficulty?: string;
  };
  signup: Record<string, never>;
  login: Record<string, never>;
  free_trial_started: Record<string, never>;
  interview_started: {
    interviewMode?: string;
    interviewType?: string;
    durationMinutes?: number;
    plan?: string;
  };
  interview_completed: {
    interviewMode?: string;
    interviewType?: string;
    durationMinutes?: number;
    plan?: string;
  };
  feedback_generated: {
    interviewMode?: string;
    interviewType?: string;
  };
  pricing_viewed: {
    source?: string;
  };
  data_analytics_pricing_viewed: {
    source?: string;
  };
  cloud_devops_pricing_viewed: {
    source?: string;
  };
  mlops_pricing_viewed: {
    source?: string;
  };
  security_pricing_viewed: {
    source?: string;
  };
  checkout_started: {
    source?: string;
    plan?: string;
    billingPeriod?: string;
  };
  purchase: {
    plan?: string;
    billingPeriod?: string;
  };
  payment_failed: {
    plan?: string;
    billingPeriod?: string;
    reason?: string;
  };
  free_15_minute_interview_section_viewed: {
    pageType?: Free15MinuteInterviewPageType;
    section: AnalyticsPageSection;
  };
  cta_clicked: {
    ctaSurface?: string;
    journey?: string;
  };
  mock_interview_funnel_step: {
    step: MockInterviewFunnelSetupStep;
    /** Absent on older events; present on everything emitted after #570. */
    entry?: MockInterviewEntry;
  };
  dussehra_page_viewed: Record<string, never>;
  dussehra_hero_viewed: Record<string, never>;
  dussehra_offer_viewed: Record<string, never>;
  dussehra_opportunity_viewed: Record<string, never>;
  dussehra_faq_viewed: Record<string, never>;
  dussehra_footer_viewed: Record<string, never>;
  dussehra_cta_clicked: {
    cta: DussehraCtaName;
  };
  dussehra_checkout_started: {
    plan?: string;
  };
  data_analytics_page_viewed: CampaignLanderPageViewedProperties;
  data_analytics_cta_clicked: CampaignLanderCtaClickedProperties;
  data_analytics_checkout_started: CampaignLanderCheckoutStartedProperties;
  cloud_devops_page_viewed: CampaignLanderPageViewedProperties;
  cloud_devops_cta_clicked: CampaignLanderCtaClickedProperties;
  cloud_devops_checkout_started: CampaignLanderCheckoutStartedProperties;
  mlops_page_viewed: CampaignLanderPageViewedProperties;
  mlops_cta_clicked: CampaignLanderCtaClickedProperties;
  mlops_checkout_started: CampaignLanderCheckoutStartedProperties;
  security_page_viewed: CampaignLanderPageViewedProperties;
  security_cta_clicked: CampaignLanderCtaClickedProperties;
  security_checkout_started: CampaignLanderCheckoutStartedProperties;
};

export type AnalyticsPropertiesFor<E extends AnalyticsEventName> =
  AnalyticsEventPropertiesByName[E];

/** Union of all event-specific property objects. Prefer `AnalyticsPropertiesFor<E>`. */
export type AnalyticsEventProperties =
  AnalyticsEventPropertiesByName[AnalyticsEventName];

export type AnalyticsDevice = "mobile" | "tablet" | "desktop";

export type AnalyticsUtm = {
  source?: string | null;
  medium?: string | null;
  campaign?: string | null;
  content?: string | null;
  term?: string | null;
};

export type AnalyticsContext = {
  pageUrl: string | null;
  referrer: string | null;
  country: string | null;
  device: AnalyticsDevice | null;
};

export type AnalyticsEventPayload<E extends AnalyticsEventName = AnalyticsEventName> = {
  event: E;
  eventId: string;
  anonymousId?: string;
  sessionId: string;
  userId?: string;
  timestamp: string;
  utm: AnalyticsUtm;
  context: AnalyticsContext;
  properties: AnalyticsPropertiesFor<E>;
};
